// UpdateAskingPriceDialog · Lot M3
// Change asking_price_current avec motif obligatoire. Le net vendeur
// n'est PAS modifiable ici (règle produit — M7 uniquement).

import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter }
  from "@/components/ui/dialog";
import { fmtMoney } from "@/lib/money";

const inputCls =
  "h-11 rounded-lg bg-white px-3 text-[15px] outline-none " +
  "focus:ring-2 focus:ring-[#C8FF2F]/40 font-mono";
const inputStyle = { boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" };

export default function UpdateAskingPriceDialog({
  open, onOpenChange, pricing, onSubmit,
}) {
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const cur = pricing?.currency || "EUR";
  const currentAsking = pricing?.asking_price_current;
  const netCurrent = pricing?.net_seller_current;
  const newVal = Number(value);
  const belowNet = newVal > 0 && netCurrent != null && newVal < netCurrent;

  const submit = async (e) => {
    e.preventDefault();
    if (!newVal || newVal <= 0) return toast.error("Prix > 0 requis");
    if (!reason.trim()) return toast.error("Motif requis");
    if (belowNet) {
      return toast.error("Baisse sous le net vendeur impossible (proposition M7 requise)");
    }
    setSubmitting(true);
    try {
      await onSubmit?.({ value: newVal, reason: reason.trim() });
      setValue(""); setReason("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" data-testid="update-asking-dialog">
        <DialogHeader>
          <DialogTitle className="text-[18px] font-semibold tracking-[-0.01em]">
            Modifier le prix affiché
          </DialogTitle>
          <p className="text-[12.5px] text-black/50">
            La nouvelle valeur ne peut pas descendre sous le net vendeur.
            Le motif est enregistré dans l&apos;historique.
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="mt-3 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-black/[0.02] p-3">
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-black/45">
                Prix actuel
              </div>
              <div className="mt-1 font-mono text-[16px] text-black"
                   data-testid="update-current-asking-value">
                {fmtMoney(currentAsking, cur)}
              </div>
            </div>
            <div className="rounded-xl bg-black/[0.02] p-3">
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-black/45">
                Net vendeur (plancher)
              </div>
              <div className="mt-1 font-mono text-[16px] text-black/70">
                {fmtMoney(netCurrent, cur)}
              </div>
            </div>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.14em] text-black/55">
              Nouveau prix affiché <span className="text-[#EF4444]">*</span>
            </span>
            <input
              type="number" min={1} step={1}
              value={value} onChange={(e) => setValue(e.target.value)}
              placeholder={String(currentAsking || "")}
              className={inputCls} style={inputStyle}
              autoFocus
              data-testid="update-asking-value-input"
            />
            {belowNet && (
              <span className="text-[11.5px] text-[#B91C1C]">
                ⚠ Nouveau prix &lt; net vendeur ({fmtMoney(netCurrent, cur)}) —
                proposition vendeur (M7) requise.
              </span>
            )}
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.14em] text-black/55">
              Motif <span className="text-[#EF4444]">*</span>
            </span>
            <input
              type="text"
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Ajustement marché, alignement concurrence…"
              className="h-11 rounded-lg bg-white px-3 text-[13.5px] outline-none focus:ring-2 focus:ring-[#C8FF2F]/40"
              style={inputStyle}
              data-testid="update-asking-reason-input"
            />
          </label>

          <DialogFooter className="mt-2 gap-2 sm:justify-end">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-full bg-white px-4 py-2 text-[13px] font-medium text-black/70 hover:text-black"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
              data-testid="update-asking-cancel-btn"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || belowNet}
              className="rounded-full bg-black px-5 py-2 text-[13px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
              data-testid="update-asking-submit-btn"
            >
              {submitting ? "Enregistrement…" : "Enregistrer"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
